import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { emitHaptic } from './haptics';

type LocationValue = { pathname: string; search: string };
type NavigateOptions = { replace?: boolean };
type Navigate = (to: string | number, options?: NavigateOptions) => void;

const LocationContext = createContext<LocationValue>({ pathname: '/', search: '' });
const NavigateContext = createContext<Navigate>(() => undefined);
const ParamsContext = createContext<Record<string, string>>({});
const OutletContext = createContext<ReactNode>(null);

function browserLocation(): LocationValue {
  return { pathname: window.location.pathname || '/', search: window.location.search || '' };
}

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<LocationValue>(browserLocation);
  useEffect(() => {
    const changed = () => { emitHaptic('navigation', { source: 'history' }); setLocation(browserLocation()); };
    window.addEventListener('popstate', changed);
    return () => window.removeEventListener('popstate', changed);
  }, []);
  const navigate = useCallback<Navigate>((to, options) => {
    if (typeof to === 'number') { emitHaptic('navigation', { source: 'history-control' }); window.history.go(to); return; }
    const url = new URL(to, window.location.origin);
    if (url.origin !== window.location.origin) { window.location.assign(url.href); return; }
    window.history[options?.replace ? 'replaceState' : 'pushState']({}, '', `${url.pathname}${url.search}${url.hash}`);
    emitHaptic('navigation', { source: 'router' });
    setLocation(browserLocation());
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  return <NavigateContext.Provider value={navigate}><LocationContext.Provider value={location}>{children}</LocationContext.Provider></NavigateContext.Provider>;
}

export function useLocation() { return useContext(LocationContext); }
export function useNavigate() { return useContext(NavigateContext); }
export function useParams<T extends Record<string, string | undefined> = Record<string, string>>() { return useContext(ParamsContext) as T; }

export function useSearchParams(): [URLSearchParams, (next: URLSearchParams | Record<string, string>, options?: NavigateOptions) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const setParams = useCallback((next: URLSearchParams | Record<string, string>, options?: NavigateOptions) => {
    const value = next instanceof URLSearchParams ? new URLSearchParams(next) : new URLSearchParams(next);
    navigate(`${location.pathname}${value.toString() ? `?${value}` : ''}`, options);
  }, [location.pathname, navigate]);
  return [params, setParams];
}

export function RouteScope({ params, outlet, children }: { params?: Record<string, string>; outlet?: ReactNode; children: ReactNode }) {
  return <ParamsContext.Provider value={params || {}}><OutletContext.Provider value={outlet || null}>{children}</OutletContext.Provider></ParamsContext.Provider>;
}

export function Outlet() { return <>{useContext(OutletContext)}</>; }

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string };

export function Link({ to, onClick, target, children, ...props }: LinkProps) {
  const navigate = useNavigate();
  function follow(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || target === '_blank') return;
    const url = new URL(to, window.location.origin);
    if (url.origin !== window.location.origin) return;
    event.preventDefault(); navigate(`${url.pathname}${url.search}${url.hash}`);
  }
  return <a href={to} target={target} onClick={follow} {...props}>{children}</a>;
}

type NavLinkProps = LinkProps & { end?: boolean; activePaths?: string[] };

export function NavLink({ to, end, activePaths = [], className, ...props }: NavLinkProps) {
  const location = useLocation();
  const targetPath = new URL(to, window.location.origin).pathname;
  const matches = (path: string, exact = false) => exact ? location.pathname === path : location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));
  const active = matches(targetPath, Boolean(end)) || activePaths.some((path) => matches(path));
  return <Link to={to} aria-current={active ? 'page' : undefined} className={[className, active ? 'active' : ''].filter(Boolean).join(' ')} {...props} />;
}

export function Navigate({ to, replace = false }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace }), [navigate, replace, to]);
  return null;
}
